import React from 'react';
import {
    ExamStatus,
    AppType,
    ApplicantDocuments,
    ProfileInfoTab,
    Card,
    PracticalQuestionsTab,
    TheoreticalQuestionsTab,
    AnimatedTabs,
    StatementTab,
} from 'components';
import { IExamApplicationDetails, IExamSessionDetails } from 'components/models';
import { Tab } from 'react-bootstrap';
import { ExpertsTab } from '../ExamDetailsTabs/ExpertsTab';
import { ExamInfoTab, ExamInfoTabProps } from '../ExamDetailsTabs/ExamInfoTab';
import { RulesTab } from '../ExamDetailsTabs/RulesTab';
import { ResultAndStatusTab } from './ResultAndStatusTab';
import styles from './ApplicantExamDetailsTabs.module.scss';

const ApplicantExamDetailsTabs = ({
    tab,
    examDetails,
    withoutExpertsTab,
    withoutInfoTab,
    withoutRulesTab,
    appType,
    onlyResultAndStatus,
    resultAndStatusCardCategory,
    resultAndStatusCardTitle,
    examSessionDetails,
    inModal,
    examApplication,
    renderResultAndStatusChild,
    onChangeDocumentSwitch,
}: IProps) => {
    const isExpert = appType === AppType.Expert;
    const isSpkfr = appType === AppType.Spkfr;
    const isApplicant = appType === AppType.Applicant;
    const isQec = appType === AppType.Qec;

    const isProtocols = () => examStatus === ExamStatus.FinalizedResults
        || examStatus === ExamStatus.AkProtocolSigned
        || examStatus === ExamStatus.NokProtocolSigned
        || examStatus === ExamStatus.SovietProtocolSignedByMembers
        || examStatus === ExamStatus.SovietProtocolSignedByChairman;

    const checkTab = () => {
        if (tab) {
            if (TabKey[tab] === TabKey.Experts && withoutExpertsTab) return null;

            return TabKey[tab];
        }

        return null;
    };

    const getDefaultTab = () => {
        if (tab) return checkTab();
        if (!isExpert && !withoutInfoTab) return TabKey.Info;
        if (withoutInfoTab && examApplication) return TabKey.ResultAndStatus;
        if (examStatus === ExamStatus.IsApplying) return TabKey.Info;
        if (!withoutExpertsTab) return TabKey.Experts;

        return TabKey.Info;
    };

    React.useEffect(() => {
        setActiveTab(getDefaultTab());
    }, [examDetails]);

    const [activeTab, setActiveTab] = React.useState(getDefaultTab());
    const examStatus = examDetails.status;
    const showDetails = (isSpkfr || isApplicant || isQec) ? true : !isProtocols();

    const renderResultAndStatus = () => (examApplication ? (
        <ResultAndStatusTab
          cardTitle={resultAndStatusCardTitle}
          cardCategory={resultAndStatusCardCategory}
          data={{
                ...examApplication,
                ...examApplication.exam,
                examStatus: examApplication.exam.status,
                examApplicationStatus: examApplication.status,
            }}
          appType={appType}
          withoutPadding={inModal}
        />
    ) : null);

    if (onlyResultAndStatus) return renderResultAndStatus();

    return (
        <>
            <AnimatedTabs
              id="applicantExamDetailsTabsTabs"
              activeKey={activeTab}
              onSelect={(key: string) => setActiveTab(key as TabKey)}
              inModal={inModal}
            >
                {!withoutInfoTab && showDetails ? (
                    <Tab eventKey={TabKey.Info} title="Информация" className="tab mt-5 p-0">
                        <ExamInfoTab appType={appType} examDetails={examDetails} withoutPadding={inModal} />
                    </Tab>
                ) : null}

                {examApplication ? (
                    <Tab eventKey={TabKey.ResultAndStatus} title="Результат и статус" className="tab mt-5 p-0">
                        {renderResultAndStatus()}
                        {renderResultAndStatusChild ? renderResultAndStatusChild() : null}
                    </Tab>
                ) : null}

                {examApplication && examApplication.recommendations ? (
                    <Tab eventKey={TabKey.RecommendationTab} title="Рекомендация" className="tab mt-5 p-0">
                        <span className={styles.recommendation}>{examApplication.recommendations}</span>
                    </Tab>
                ) : null}

                {examApplication && examApplication.applicant && examApplication.applicantIdentityDoc ? (
                    <Tab eventKey={TabKey.Identification} title="Паспорт" className="tab mt-5 p-0">
                        <Card withoutPadding={inModal} withoutBorder>
                            <ProfileInfoTab
                              applicant={examApplication.applicant}
                              applicantIdentityDoc={examApplication.applicantIdentityDoc}
                            />
                        </Card>
                    </Tab>
                ) : null}

                {!withoutExpertsTab && showDetails ? (
                    <Tab eventKey={TabKey.Experts} title="Эксперты" className="tab mt-5 p-0">
                        <ExpertsTab experts={examDetails.experts} appType={appType} />
                    </Tab>
                ) : null}

                {!withoutRulesTab && showDetails ? (
                    <Tab eventKey={TabKey.Rules} title="Правила проведения" className="tab mt-5 p-0">
                        <RulesTab rules={examDetails.rules} />
                    </Tab>
                ) : null}

                {examApplication && examApplication.documents ? (
                    <Tab eventKey={TabKey.Documents} title="Документы" className="tab mt-5 p-0">
                        <Card
                          title={!inModal ? 'Сканы документов' : undefined}
                          category={!inModal ? 'Сканы предоставленных в ЦОК документов' : undefined}
                          withoutPadding
                          withoutBorder
                        >
                            <ApplicantDocuments withoutPadding={inModal} documents={examApplication.documents} onChangeSwitch={onChangeDocumentSwitch} />
                        </Card>
                    </Tab>
                ) : null}

                {examApplication && examApplication.signCode ? (
                    <Tab eventKey={TabKey.StatementInfoTab} title="Заявление" className="tab mt-5 p-0">
                        <StatementTab examApplication={examApplication} withoutPadding={inModal} />
                    </Tab>
                ) : null}

                {examSessionDetails && examSessionDetails.practicalQuestions && examApplication && isSpkfr ? (
                    <Tab eventKey={TabKey.PracticalQuestions} title="Практическая часть" className="tab mt-4 p-0">
                        <Card contentClassName="pt-0" withoutPadding={inModal} withoutBorder>
                            <PracticalQuestionsTab
                              examSessionDetails={examSessionDetails}
                              finalized={examApplication.finalized}
                              applicantStatus={examApplication.status}
                            />
                        </Card>
                    </Tab>
                ) : null}

                {examSessionDetails && examSessionDetails.theoreticalQuestions && examApplication && isSpkfr ? (
                    <Tab eventKey={TabKey.TheoreticalQuestions} title="Теоретическая часть" className="tab mt-4 p-0">
                        <Card contentClassName="pt-0" withoutPadding={inModal} withoutBorder>
                            <TheoreticalQuestionsTab
                              examSessionDetails={examSessionDetails}
                              finalized={examApplication.finalized}
                            />
                        </Card>
                    </Tab>
                ) : null}
            </AnimatedTabs>
        </>
    );
};

export default ApplicantExamDetailsTabs;

export interface IProps {
    examDetails: ExamInfoTabProps['examDetails'];
    appType: AppType;
    examApplication?: IExamApplicationDetails | null;
    examSessionDetails?: IExamSessionDetails | null;
    withoutExpertsTab?: boolean;
    withoutInfoTab?: boolean;
    withoutRulesTab?: boolean;
    onlyResultAndStatus?: boolean;
    resultAndStatusCardTitle?: string | null;
    resultAndStatusCardCategory?: string | null;
    inModal?: boolean;
    tab?: TabType;
    renderResultAndStatusChild?: () => React.ReactNode;
    onChangeDocumentSwitch?: (change: boolean[]) => void;
}

enum TabKey {
    Experts = 'experts',
    Info = 'info',
    Rules = 'rules',
    Documents = 'documents',
    ResultAndStatus = 'resultAndStatus',
    Identification = 'identification',
    PracticalQuestions = 'practicalQuestions',
    TheoreticalQuestions = 'theoreticalQuestions',
    StatementInfoTab = 'statementInfo',
    RecommendationTab = 'recommendation',
}

type TabType = keyof typeof TabKey;
